import { Permissions, Permission } from './permissions.constants';

export interface IPermissionItem {
  key: Permission;
  labelAr: string;
  status?: 'pending';
}

export interface IPermissionModule {
  module: string;
  labelAr: string;
  icon: string;
  items: IPermissionItem[];
}

export const PERMISSION_MODULES: IPermissionModule[] = [
  {
    module: 'dashboard',
    labelAr: 'لوحة التحكم',
    icon: 'pi pi-th-large',
    items: [
      { key: Permissions.DASHBOARD_VIEW,              labelAr: 'عرض لوحة التحكم' },
      { key: Permissions.DASHBOARD_WIDGET_KPI,        labelAr: 'ويدجت مؤشرات الأداء' },
      { key: Permissions.DASHBOARD_WIDGET_PROCESSES,  labelAr: 'ويدجت العمليات' },
      { key: Permissions.DASHBOARD_WIDGET_GOALS,      labelAr: 'ويدجت الأهداف' },
      { key: Permissions.DASHBOARD_WIDGET_RISKS,      labelAr: 'ويدجت المخاطر' },
      { key: Permissions.DASHBOARD_WIDGET_COMPLAINTS, labelAr: 'ويدجت الشكاوى' },
      { key: Permissions.DASHBOARD_WIDGET_AUDIT,      labelAr: 'ويدجت التدقيق' },
      { key: Permissions.DASHBOARD_WIDGET_TASKS,      labelAr: 'ويدجت المهام' },
      { key: Permissions.DASHBOARD_WIDGET_MEETINGS,   labelAr: 'ويدجت الاجتماعات' },
      { key: Permissions.DASHBOARD_WIDGET_TRAINING,   labelAr: 'ويدجت التدريب' },
    ],
  },
  {
    module: 'process',
    labelAr: 'إدارة العمليات',
    icon: 'pi pi-sitemap',
    items: [
      { key: Permissions.PROCESS_VIEW,          labelAr: 'عرض العمليات' },
      { key: Permissions.PROCESS_CREATE,        labelAr: 'إنشاء عملية' },
      { key: Permissions.PROCESS_EDIT,          labelAr: 'تعديل العملية' },
      { key: Permissions.PROCESS_DELETE,        labelAr: 'حذف العملية' },
      { key: Permissions.PROCESS_SEND_REVIEW,   labelAr: 'إرسال للمراجعة' },
      { key: Permissions.PROCESS_REVIEW,        labelAr: 'مراجعة العملية' },
      { key: Permissions.PROCESS_APPROVE,       labelAr: 'اعتماد العملية' },
      { key: Permissions.PROCESS_FINAL_APPROVE, labelAr: 'الاعتماد النهائي للعملية' },
    ],
  },
  {
    module: 'goals',
    labelAr: 'أهداف الجودة',
    icon: 'pi pi-flag',
    items: [
      // TODO(confirm-with-basem): reconciled 2026-09-04 to the real backend's
      // 3-permission model (quality-objectives.create/.view/.system-manager) —
      // see permissions.constants.ts. Edit/delete/approve/final-approve/followup
      // are all covered server-side by .create/.view/.system-manager, not by
      // dedicated permission strings, so their standalone rows are removed here.
      { key: Permissions.GOALS_VIEW,           labelAr: 'عرض أهداف الجودة' },
      { key: Permissions.QUALITY_OBJECTIVES_CREATE,         labelAr: 'إنشاء / تعديل هدف جودة' },
      { key: Permissions.GOALS_SYSTEM_MANAGER, labelAr: 'الاعتماد النهائي لأهداف الجودة (مدير النظام)' },
    ],
  },
  {
    module: 'kpi',
    labelAr: 'مؤشرات الأداء',
    icon: 'pi pi-chart-bar',
    items: [
      { key: Permissions.KPI_VIEW,    labelAr: 'عرض مؤشرات الأداء' },
      { key: Permissions.KPI_CREATE,  labelAr: 'إنشاء مؤشر أداء' },
      { key: Permissions.KPI_EDIT,    labelAr: 'تعديل مؤشر الأداء' },
      { key: Permissions.KPI_REVIEW,  labelAr: 'مراجعة قياس مؤشر الأداء' },
      { key: Permissions.KPI_APPROVE, labelAr: 'اعتماد مؤشر الأداء' },
    ],
  },
  {
    module: 'risk',
    labelAr: 'إدارة المخاطر',
    icon: 'pi pi-exclamation-triangle',
    items: [
      { key: Permissions.RISK_OPPORTUNITY_VIEW,          labelAr: 'عرض المخاطر والفرص' },
      { key: Permissions.RISK_OPPORTUNITY_CREATE,        labelAr: 'إنشاء مخاطرة أو فرصة' },
      { key: Permissions.RISK_OPPORTUNITY_REVIEW,        labelAr: 'مراجعة المخاطر والفرص' },
      { key: Permissions.RISK_OPPORTUNITY_APPROVE,       labelAr: 'اعتماد المخاطرة أو الفرصة' },
      { key: Permissions.RISK_OPPORTUNITY_FINAL_APPROVE, labelAr: 'الاعتماد النهائي للمخاطرة أو الفرصة' },
    ],
  },
  {
    module: 'audit',
    labelAr: 'التدقيق الداخلي',
    icon: 'pi pi-search',
    items: [
      { key: Permissions.AUDIT_VIEW,    labelAr: 'عرض التدقيق الداخلي' },
      { key: Permissions.AUDIT_CREATE,  labelAr: 'إنشاء تدقيق' },
      { key: Permissions.AUDIT_EDIT,    labelAr: 'تعديل التدقيق' },
      { key: Permissions.AUDIT_APPROVE, labelAr: 'اعتماد التدقيق' },
      // Fine-grained execution-level permissions, requested explicitly for
      // an internal demo (2026-09-07) — see PROJECT-MAP.md's permission-gap
      // finding: the backend does not issue these two as real per-user
      // grants yet (only the 5 real internal-audit.* permissions exist
      // server-side today), so toggling these checkboxes here is UI-only
      // until the backend adds them.
      { key: Permissions.AUDIT_EXECUTION_VISIT_OPEN,  labelAr: 'افتتاح الزيارة' },
      { key: Permissions.AUDIT_EXECUTION_VISIT_CLOSE, labelAr: 'اختتام الزيارة' },
    ],
  },
  {
    module: 'complaint',
    labelAr: 'الشكاوى والمقترحات',
    icon: 'pi pi-comments',
    items: [
      { key: Permissions.COMPLAINT_VIEW,    labelAr: 'عرض الشكاوى والمقترحات' },
      { key: Permissions.COMPLAINT_CREATE,  labelAr: 'إنشاء شكوى أو مقترح' },
      { key: Permissions.COMPLAINT_EDIT,    labelAr: 'تعديل الشكوى أو المقترح' },
      { key: Permissions.COMPLAINT_APPROVE, labelAr: 'اعتماد الشكوى أو المقترح' },
    ],
  },
  {
    module: 'survey',
    labelAr: 'الاستبيانات',
    icon: 'pi pi-file',
    items: [
      { key: Permissions.SURVEY_VIEW,   labelAr: 'عرض الاستبيانات' },
      { key: Permissions.SURVEY_CREATE, labelAr: 'إنشاء استبيان' },
      { key: Permissions.SURVEY_EDIT,   labelAr: 'تعديل الاستبيان' },
    ],
  },
  {
    module: 'training',
    labelAr: 'إدارة التدريب',
    icon: 'pi pi-book',
    items: [
      { key: Permissions.TRAINING_VIEW,   labelAr: 'عرض برامج التدريب' },
      { key: Permissions.TRAINING_CREATE, labelAr: 'إنشاء برنامج تدريبي' },
      { key: Permissions.TRAINING_EDIT,   labelAr: 'تعديل برنامج التدريب' },
    ],
  },
  {
    module: 'meeting',
    labelAr: 'إدارة الاجتماعات',
    icon: 'pi pi-calendar',
    items: [
      { key: Permissions.MEETING_VIEW,   labelAr: 'عرض الاجتماعات' },
      { key: Permissions.MEETING_CREATE, labelAr: 'إنشاء اجتماع' },
      { key: Permissions.MEETING_EDIT,   labelAr: 'تعديل الاجتماع' },
    ],
  },
  {
    module: 'task',
    labelAr: 'إدارة المهام',
    icon: 'pi pi-check-square',
    items: [
      { key: Permissions.TASK_VIEW,   labelAr: 'عرض المهام' },
      { key: Permissions.TASK_CREATE, labelAr: 'إنشاء مهمة' },
      { key: Permissions.TASK_EDIT,   labelAr: 'تعديل المهمة' },
    ],
  },
  {
    module: 'knowledge',
    labelAr: 'إدارة المعرفة',
    icon: 'pi pi-database',
    items: [
      { key: Permissions.KNOWLEDGE_VIEW,   labelAr: 'عرض قاعدة المعرفة' },
      { key: Permissions.KNOWLEDGE_CREATE, labelAr: 'إنشاء محتوى معرفي' },
      { key: Permissions.KNOWLEDGE_EDIT,   labelAr: 'تعديل المحتوى المعرفي' },
    ],
  },
  {
    module: 'org',
    labelAr: 'الهيكل التنظيمي',
    icon: 'pi pi-building',
    items: [
      { key: Permissions.ORG_VIEW,   labelAr: 'عرض الهيكل التنظيمي' },
      { key: Permissions.ORG_CREATE, labelAr: 'إنشاء وحدة تنظيمية' },
      { key: Permissions.ORG_EDIT,   labelAr: 'تعديل الوحدة التنظيمية' },
    ],
  },
  {
    module: 'settings',
    labelAr: 'الإعدادات',
    icon: 'pi pi-cog',
    items: [
      { key: Permissions.SETTINGS_VIEW, labelAr: 'عرض الإعدادات' },
      { key: Permissions.SETTINGS_EDIT, labelAr: 'تعديل الإعدادات' },
    ],
  },
  {
    module: 'users',
    labelAr: 'إدارة المستخدمين',
    icon: 'pi pi-users',
    items: [
      { key: Permissions.USERS_VIEW,   labelAr: 'عرض المستخدمين' },
      { key: Permissions.USERS_CREATE, labelAr: 'إنشاء مستخدم' },
      { key: Permissions.USERS_EDIT,   labelAr: 'تعديل المستخدم' },
      { key: Permissions.USERS_DELETE, labelAr: 'حذف المستخدم' },
    ],
  },
];

export const TOTAL_PERMISSIONS_COUNT = PERMISSION_MODULES.reduce(
  (sum, g) => sum + g.items.length,
  0
);
